/** @jsxImportSource @emotion/react */
import {
  createStyles,
  List,
  ListItem,
  useTheme,
  Typography,
  Icon,
  Modal,
} from '@quickbit/qb-design-system';
import { useState } from 'react';
import { type CorporateWalletTransactionMinimal } from 'api/walletApi.types';
import { formatCurrency } from 'utils';
import RenderStatusTooltip from './RenderStatusTooltip';
import { TransactionDetails } from './TransactionDetails';
import { mapTransactionTypeToText, mapTransactionTypeToIcon } from './utils';

interface Props {
  transactions: CorporateWalletTransactionMinimal[];
  merchantId: string;
}

export const TransactionList = ({ transactions, merchantId }: Props) => {
  const styles = useStyles();
  const { palette, spacing } = useTheme();

  const [selectedTransactionId, setSelectedTransactionId] = useState<string>();

  const getListItemTitle = (transaction: CorporateWalletTransactionMinimal) => {
    return (
      <div
        css={{
          display: 'flex',
          columnGap: spacing.xxxs,
          alignItems: 'center',
        }}
      >
        {mapTransactionTypeToText(transaction.type)}
      </div>
    );
  };
  return (
    <div css={styles.transactionList}>
      <List css={{ overflowY: 'scroll', width: '100%' }}>
        {transactions.map((transaction: CorporateWalletTransactionMinimal) => {
          return (
            <ListItem
              css={styles.listItem}
              onClick={() => {
                setSelectedTransactionId(
                  selectedTransactionId === transaction.id
                    ? undefined
                    : transaction.id
                );
              }}
              icon={
                <div
                  css={{
                    height: spacing.s,
                    width: spacing.s,
                    borderRadius: '50%',
                    background: palette.grey.x100,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Icon
                    name={mapTransactionTypeToIcon(transaction.type)}
                    size={`calc(${spacing.xxs} + calc(${spacing.xxxs}/4))`}
                  />
                </div>
              }
              key={transaction.id}
              title={getListItemTitle(transaction)}
              subtitle={
                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography variant="bodySmall" color={palette.grey.default}>
                    {new Date(transaction.createdAt).toLocaleString()}
                  </Typography>
                  {transaction.authorizedBy && (
                    <Typography
                      variant="bodySmallest"
                      color={palette.grey.default}
                    >
                      {`Initiated by: ${transaction.authorizedBy.email}`}
                    </Typography>
                  )}
                </div>
              }
              content={formatCurrency(transaction.amount)}
              subcontent={
                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography variant="bodySmall" color={palette.grey.default}>
                    {formatCurrency(transaction.amountReference)}
                  </Typography>
                </div>
              }
              action={
                <RenderStatusTooltip
                  status={transaction.status}
                  transactionType={transaction.type}
                />
              }
            />
          );
        })}
      </List>
      {selectedTransactionId && (
        <Modal
          closeModal={() => {
            setSelectedTransactionId(undefined);
          }}
        >
          <TransactionDetails
            merchantId={merchantId}
            transactionId={selectedTransactionId}
            authorizedBy={
              transactions.find((t) => t.id === selectedTransactionId)
                ?.authorizedBy
            }
          />
        </Modal>
      )}
    </div>
  );
};

const useStyles = createStyles(({ palette, spacing, breakpoints }) => ({
  listItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: palette.grey.x50,
    },
  },
  transactionList: {
    marginTop: spacing.xs,
    marginBottom: spacing.xs,
    height: '100%',
    [breakpoints.laptopSmall]: {
      marginBottom: 0,
    },
  },
}));
